import { createNoteNode } from '../nodeCreationHelpers'
import { createImgTag, wrapP, wrapTag } from '../html_utility'
import { IDocUnitLesson, IUnitLessonParams } from './types'
import { getLessonNum } from './utility'

export function createSnapshot(params: IUnitLessonParams) {
  const table = createTable()
  insertHeaderRow(table)
  insertContentRows(table, params)

  return [
    createNoteNode({
      title: 'Learning Plan Snapshot',
      value: table.outerHTML,
      studentFacing: false,
    }),
  ]
}

function createTable() {
  const table = document.createElement('table')
  table.setAttribute('style', 'border-collapse: collapse;')
  return table
}

const centerStyles = ['text-align: center']

const baseStyles = [
  'border-style: solid',
  'border-color: #000000',
  'vertical-align: top',
]

const headerCellStyle = [...centerStyles, ...baseStyles]

const centeredIndexes = [0, 1, 3, 4]

const headerCells: [string, number][] = [
  ['Part', 6.57],
  ['Duration', 12.22],
  ['Summary', 41.91],
  ['', 6.22],
  ['Slide', 7.92],
  ['Material', 25.09],
]

function insertHeaderRow(table: HTMLTableElement) {
  const headerRow = table.insertRow()
  headerCells.forEach(([label, width]) => {
    const cell = headerRow.insertCell()
    cell.innerText = label
    cell.setAttribute(
      'style',
      [`width: ${width}%`, ...headerCellStyle].join('; '),
    )
  })
}

function insertContentRows(table: HTMLTableElement, params: IUnitLessonParams) {
  const lessonData = getLessonData(params.metadata)

  let currentDay: number | undefined
  lessonData.activities.forEach((activity, activityIndex) => {
    if (currentDay === undefined) {
      currentDay = activity.day
    } else if (currentDay !== activity.day) {
      addDayRow(currentDay, table)
      currentDay = activity.day
    }

    addContentRow(activity, table, activityIndex + 1, params.lessonElement)
  })
}

function addContentRow(
  activity: ActivityData,
  table: HTMLTableElement,
  index: number,
  lessonElement: HTMLElement,
) {
  const row = table.insertRow()
  getRowTextForActivity(activity, index, lessonElement).forEach(
    (t, cellIndex) => {
      const cell = row.insertCell()
      cell.innerHTML = t
      const styles = [...baseStyles]
      if (centeredIndexes.includes(cellIndex)) {
        styles.push(...centerStyles)
      }
      cell.setAttribute('style', styles.join('; '))
    },
  )
}

type ActivityData = {
  name: string
  elementId: string
  day: number
  description: string
  material: { name: string; url: string | undefined }[]
  timeMinutes: number
  slides: string
}

function getRowTextForActivity(
  activity: ActivityData,
  index: number,
  lessonElement: HTMLElement,
) {
  const { name, description, material, slides, timeMinutes } = activity
  return [
    String(index),
    `${timeMinutes} min`,
    `${wrapP(name.toUpperCase())}${wrapP(description)}`,
    hasCheckmark(activity, lessonElement),
    slides,
    getMaterialItems(material),
  ]
}

function getMaterialItems(
  material: { name: string; url: string | undefined }[],
) {
  return material
    .filter(({ name }) => name !== 'science notebook')
    .map(({ name, url }) => {
      if (name.startsWith('https:')) {
        return `<a href="${name}">${name}</a>`
      }
      if (!url) return name
      return `<a href="${url}">${name}</a>`
    })
    .join(', ')
}

function hasCheckmark(activity: ActivityData, lessonElement: HTMLElement) {
  const activityElement = lessonElement.querySelector(activity.elementId)
  if (activityElement?.outerHTML.includes('assessment_icon.png')) {
    return createImgTag({
      src: 'https://kiddom-media-development.s3.amazonaws.com/OUR/Math/7/assessment_icon.png',
      attributes: { alt: 'assessment', width: '30' },
    })
  }
  return ''
}

const dayCellStyle = [
  ...baseStyles,
  'width: 100%',
  'text-align: right',
  'background-color: #000000',
]

function addDayRow(day: number, table: HTMLTableElement) {
  const row = table.insertRow()
  const cell = row.insertCell()
  cell.setAttribute('style', dayCellStyle.join('; '))
  cell.colSpan = headerCells.length
  cell.innerHTML = wrapTag(
    'strong',
    wrapTag('span', `End of Day ${day}`, { style: 'color: #ffffff;' }),
  )
}

function getLessonData(metadata: IDocUnitLesson) {
  return {
    name: metadata.name,
    lessonNum: getLessonNum(metadata),
    activities: metadata.hasPart.map((activityData) => {
      return {
        elementId: activityData['@id'],
        name: activityData.name,
        description: activityData.description,
        day: activityData.day, // group by day for table layout
        material: activityData.material.map(({ name, url }) => ({ name, url })),
        slides: activityData.slideId,
        timeMinutes: activityData.timeRequired,
      }
    }),
  }
}
